import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Button, buttonClassName } from "@/components/ui/Button";
import { CheckboxInput } from "@/components/ui/Checkbox";
import { Dialog } from "@/components/ui/Dialog";
import { Sheet } from "@/components/ui/Sheet";
import { ApiError, apiFetch } from "@/lib/api";
import type { ExportColumnsResource, ExportEntityResource } from "@/lib/api-types";
import { useIsPhone } from "@/lib/use-media-query";

/**
 * Story 15.4's column picker (R-I2's backlog item 4: a tailored export
 * without deleting 190-odd unwanted columns by hand from the full one).
 * Additive alongside `BackupStatus.tsx`'s own existing "Export portfolio"
 * one-click full export (`GET /exports/portfolio.zip`, untouched) — this
 * dialog's own "Download ZIP" action is a second, separate trigger, never a
 * replacement.
 *
 * The column universe shown here is always the real, current one:
 * `GET /exports/columns` (`app/api/exports.py::list_export_columns`, built
 * straight from `csv_shape.ENTITY_SPECS`) is fetched fresh every time the
 * dialog opens — never a hand-maintained constant in this file that could
 * silently drift from what the backend actually exports (VC-15.4-06).
 *
 * The download itself is a plain `<a href>` navigation carrying the
 * selection as repeated `?column=entity.column` query parameters (spec's
 * own default approach, confirmed workable here: even every one of the
 * ~195 real columns selected at once produces a query string of a few
 * thousand characters, comfortably under any realistic URL-length limit —
 * see this story's own worker report for the measurement) — never
 * `apiFetch`, mirroring `BackupStatus.tsx`'s own identical reasoning for
 * `/exports/portfolio.zip`: a large streamed ZIP is never a good `fetch`
 * response shape, and `csrf-header-guard.test.ts` forbids a bare `fetch`
 * outside `lib/api.ts` regardless.
 */
export function ColumnPickerDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const isPhone = useIsPhone();
  const Container = isPhone ? Sheet : Dialog;

  const columnsQuery = useQuery({
    queryKey: ["export-columns"],
    queryFn: () => apiFetch<ExportColumnsResource>("/exports/columns"),
    enabled: open,
  });

  const [selected, setSelected] = useState<Record<string, Set<string>>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) {
      setSelected({});
      setExpanded(new Set());
    }
  }, [open]);

  function toggleExpanded(entityKey: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(entityKey)) {
        next.delete(entityKey);
      } else {
        next.add(entityKey);
      }
      return next;
    });
  }

  function toggleColumn(entityKey: string, columnName: string) {
    setSelected((prev) => {
      const current = new Set(prev[entityKey] ?? []);
      if (current.has(columnName)) {
        current.delete(columnName);
      } else {
        current.add(columnName);
      }
      return { ...prev, [entityKey]: current };
    });
  }

  function setEntireEntity(entity: ExportEntityResource, select: boolean) {
    setSelected((prev) => ({
      ...prev,
      [entity.key]: select ? new Set(entity.columns.map((column) => column.name)) : new Set(),
    }));
  }

  const entities = columnsQuery.data?.entities ?? [];
  const totalSelected = Object.values(selected).reduce((count, set) => count + set.size, 0);

  const params = new URLSearchParams();
  for (const [entityKey, columnNames] of Object.entries(selected)) {
    for (const columnName of columnNames) {
      params.append("column", `${entityKey}.${columnName}`);
    }
  }
  const downloadHref = `/exports/portfolio-columns.zip?${params.toString()}`;
  const canDownload = totalSelected > 0;

  const errorMessage = columnsQuery.error instanceof ApiError ? columnsQuery.error.message : undefined;

  return (
    <Container open={open} onOpenChange={onOpenChange} title="Choose columns">
      <div className="mt-4 flex flex-col gap-4">
        <p className="text-body text-ink-secondary">
          Pick exactly the columns you want, grouped by record type. Anything left unchecked for a record
          type is left out of that file entirely — and a record type with nothing checked doesn&apos;t get a
          file at all.
        </p>

        {columnsQuery.isError ? (
          <div className="rounded-lg border border-border-hairline bg-surface p-4 text-body text-ink">
            <p>Couldn&apos;t load the column list.</p>
            {errorMessage ? <p className="mt-1 text-ink-secondary">{errorMessage}</p> : null}
          </div>
        ) : columnsQuery.isPending ? (
          <p className="text-body text-ink-secondary">Loading columns…</p>
        ) : (
          <div className="flex max-h-[50vh] flex-col gap-2 overflow-y-auto pr-1">
            {entities.map((entity) => {
              const entitySelected = selected[entity.key] ?? new Set<string>();
              const isExpanded = expanded.has(entity.key);
              const allSelected = entity.columns.length > 0 && entity.columns.every((c) => entitySelected.has(c.name));

              return (
                <div key={entity.key} className="rounded-lg border border-border-hairline">
                  <div className="flex items-center justify-between gap-2 px-4 py-3">
                    <button
                      type="button"
                      aria-expanded={isExpanded}
                      onClick={() => toggleExpanded(entity.key)}
                      className="flex min-w-0 flex-1 items-center gap-2 text-left"
                    >
                      <svg
                        width={12}
                        height={12}
                        viewBox="0 0 12 12"
                        aria-hidden="true"
                        className={isExpanded ? "shrink-0 rotate-90" : "shrink-0"}
                      >
                        <path d="M4 2.5 8 6l-4 3.5" fill="none" stroke="currentColor" strokeWidth={1.5} />
                      </svg>
                      <span className="min-w-0 flex-1">
                        <span className="block text-body font-medium text-ink">{entity.filename}</span>
                        <span className="block truncate text-meta text-ink-secondary">{entity.description}</span>
                      </span>
                    </button>
                    <label className="flex shrink-0 items-center gap-2 text-meta text-ink-secondary">
                      <CheckboxInput
                        aria-label={`Select all columns for ${entity.filename}`}
                        checked={allSelected}
                        onChange={() => setEntireEntity(entity, !allSelected)}
                      />
                      {entitySelected.size} of {entity.columns.length}
                    </label>
                  </div>

                  {isExpanded ? (
                    <div className="grid grid-cols-1 gap-x-4 gap-y-2 border-t border-border-hairline px-4 py-3 sm:grid-cols-2">
                      {entity.columns.map((column) => (
                        <label key={column.name} className="flex items-start gap-2">
                          <CheckboxInput
                            className="mt-1"
                            checked={entitySelected.has(column.name)}
                            onChange={() => toggleColumn(entity.key, column.name)}
                          />
                          <span className="flex flex-col">
                            <span className="text-body text-ink">{column.label}</span>
                            <span className="text-meta text-ink-secondary">{column.description}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}

        <div className="flex items-center justify-between gap-2">
          <p className="text-meta text-ink-secondary">
            {totalSelected} column{totalSelected === 1 ? "" : "s"} selected
          </p>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            {canDownload ? (
              <a href={downloadHref} className={buttonClassName("primary")} onClick={() => onOpenChange(false)}>
                Download ZIP
              </a>
            ) : (
              <span className={buttonClassName("primary", "pointer-events-none opacity-50")} aria-disabled="true">
                Download ZIP
              </span>
            )}
          </div>
        </div>
      </div>
    </Container>
  );
}
